import type { AssetRegistry } from "../assets/AssetRegistry";
import type { Log } from "@slot/presentation";

export interface BrandedAssetResolverDeps {
  assets: AssetRegistry;
  /** The game's shared visuals bundle (the brandable surface). */
  sharedBundle: string;
  /** The operator bundle name inside the registry — present only when a manifest resolved successfully. */
  operatorBundle?: string | null;
  /** Paths the active operator may override; everything else is always served from the shared bundle. */
  assetOverrides: ReadonlySet<string>;
  operatorId: string | null;
  log: Log;
}

/**
 * Single lookup surface for brandable assets.
 * Serves the operator bundle FIRST when the path is allowlisted and the asset is loaded, else the
 * shared bundle. Non-branded paths are served from the shared bundle directly.
 */
export class BrandedAssetResolver {
  private readonly deps: BrandedAssetResolverDeps;

  public constructor(deps: BrandedAssetResolverDeps) {
    this.deps = deps;
  }

  public getOperatorId(): string | null {
    return this.deps.operatorId;
  }

  /** The bundle a path resolves from (operator when branded + loaded, else shared). */
  public resolveBundle(path: string): string {
    const op = this.deps.operatorBundle;
    if (op && this.deps.assetOverrides.has(path) && this.deps.assets.getLoadedAssetOrNull(op, path) !== null) return op;
    return this.deps.sharedBundle;
  }

  public getAssetOrNull<T>(path: string): T | null {
    return this.deps.assets.getLoadedAssetOrNull<T>(this.resolveBundle(path), path);
  }

  public getAssetOrThrow<T>(path: string): T {
    return this.deps.assets.getLoadedAssetOrThrow<T>(this.resolveBundle(path), path);
  }

  /** Loaded asset from a specific non-branded bundle (e.g. `shell-ui`). */
  public getFromBundleOrNull<T>(bundle: string, path: string): T | null {
    return this.deps.assets.getLoadedAssetOrNull<T>(bundle, path);
  }
}
